import React from 'react';
import { Card, Table, Tag, Button, Input, Typography, Tooltip } from 'antd';
import { ThunderboltOutlined, CaretRightOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { ObserveHandoffCheck, ObserveHandoffResult } from '../../../../services/devApi';

const { Text } = Typography;

interface Props {
  checks: ObserveHandoffCheck[];
  results: Record<string, ObserveHandoffResult>;
  docNo: string;
  runningCheckId: string | null;
  runningAll: boolean;
  onDocNoChange: (v: string) => void;
  onRunCheck: (check: ObserveHandoffCheck) => void;
  onRunAll: () => void;
}

const HandoffSection: React.FC<Props> = ({
  checks,
  results,
  docNo,
  runningCheckId,
  runningAll,
  onDocNoChange,
  onRunCheck,
  onRunAll,
}) => {
  const columns: ColumnsType<ObserveHandoffCheck> = [
    {
      title: '檢查名稱',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (name: string, record: ObserveHandoffCheck) => (
        <div>
          <Text strong>{name}</Text>
          {record.when && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{record.when}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'URL',
      dataIndex: 'urlTemplate',
      key: 'urlTemplate',
      ellipsis: { showTitle: false },
      render: (tpl: string) => {
        const preview = docNo ? tpl.split('{docNo}').join(docNo) : tpl;
        return (
          <Tooltip title={preview} placement="topLeft">
            <Text code style={{ fontSize: 11 }}>{preview}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: '結果',
      key: 'result',
      width: 140,
      render: (_: unknown, record: ObserveHandoffCheck) => {
        const r = results[record.id];
        if (!r) return <Text type="secondary">未執行</Text>;
        return r.pass ? (
          <Tag color="green">PASS</Tag>
        ) : (
          <Tooltip title={r.detail}>
            <Tag color="red">FAIL ({r.httpStatus || '--'})</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: unknown, record: ObserveHandoffCheck) => (
        <Button
          size="small"
          icon={<CaretRightOutlined />}
          loading={runningCheckId === record.id}
          disabled={runningAll || (!!runningCheckId && runningCheckId !== record.id)}
          onClick={() => onRunCheck(record)}
        >
          執行
        </Button>
      ),
    },
  ];

  return (
    <Card title="交棒驗證">
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            實際單號（替換 URL 中的 {'{docNo}'}）
          </Text>
          <Input
            value={docNo}
            onChange={(e) => onDocNoChange(e.target.value)}
            style={{ width: 220 }}
            className="font-mono"
            placeholder="例如 CH03L0611A"
          />
        </div>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={runningAll}
          disabled={checks.length === 0 || !docNo.trim()}
          onClick={onRunAll}
        >
          全部執行
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          全部執行完成後自動將結果存回後端
        </Text>
      </div>
      <Table<ObserveHandoffCheck>
        dataSource={checks}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
      />
    </Card>
  );
};

export default HandoffSection;
